import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const Announcements = () => {
  const [loading, setLoading] = useState(true);
  const [annData, setAnnData] = useState(null);
  const [error, setError] = useState(null);
  const [isMinimized, setIsMinimized] = useState(true);

  const server_url = process.env.REACT_APP_API_URI;

  const fetchPolicy = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Mock data for demonstration if API fails
      const mockData = {
        mainTitle: '🎉 Latest Updates',
        sections: [
          {
            contents: [
              { content: 'New Movies Added' },
              { content: 'Enhanced Search' },
              { content: 'Better Reviews' },
            ],
          },
        ],
      };

      if (server_url) {
        try {
          const response = await axios.get(
            `${server_url}/content/get-content/67da5f069849fb09a80211b0`,
            { timeout: 3000 },
          );

          if (response.data?.data?.mainTitle) {
            setAnnData(response.data.data);
          } else {
            setAnnData(mockData); // Fallback to mock data
          }
        } catch (apiError) {
          console.log('API unavailable, using demo content');
          setAnnData(mockData); // Use mock data if API fails
        }
      } else {
        setAnnData(mockData); // Use mock data if no server URL
      }
    } catch (error) {
      console.error('Error in announcements:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [server_url]);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  const handleToggle = () => {
    setIsMinimized(!isMinimized);
  };

  // Always show loading state
  if (loading) {
    return (
      <div className="w-full">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-gradient-to-r from-secondary/10 via-secondary/20 to-secondary/10 rounded-2xl p-4 border border-secondary/30 backdrop-blur-sm">
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-2 text-white/80">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary"></div>
                <span className="text-sm">Loading announcements...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state with retry option
  if (error && !annData) {
    return (
      <div className="w-full">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-gradient-to-r from-red/10 via-red/20 to-red/10 rounded-2xl p-4 border border-red/30 backdrop-blur-sm">
            <div className="flex items-center justify-center space-x-3">
              <span className="text-white/80 text-sm">
                Unable to load announcements
              </span>
              <button
                onClick={fetchPolicy}
                className="px-3 py-1 bg-secondary rounded-full text-primary text-xs font-semibold hover:bg-secondary/90 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if
  if (!annData) return null;

  return (
    <div className="w-full">
      <div
        className={
          !isMinimized
            ? 'max-w-6xl mx-auto px-4'
            : 'max-w-6xl mx-auto px-4 max-h-12'
        }
      >
        {/* Modern Announcement Banner */}
        <div className="relative overflow-hidden">
          {/* Main Banner */}
          <div
            className={`bg-gradient-to-r from-secondary/5 via-secondary/8 to-secondary/5 rounded-2xl border border-secondary/20 backdrop-blur-lg shadow-2xl transition-all duration-500 ${isMinimized ? 'p-3' : 'p-4 md:p-6'}`}
          >
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className=""></div>
            </div>

            {/* Content Container */}
            <div className="relative z-10">
              {/* Header Section */}

              <div className="flex justify-between ">
                <div className="flex text-center items-center space-y-1 space-x-2">
                  <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-primary"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                  <h2 className="text-white font-bold text-xl leading-none">
                    {annData.mainTitle}
                  </h2>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleToggle}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-200 backdrop-blur-sm"
                    aria-label={
                      isMinimized
                        ? 'Expand announcements'
                        : 'Minimize announcements'
                    }
                  >
                    <svg
                      className={`w-4 h-4 text-white transition-transform duration-300 ${isMinimized ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content Section - Only show when not minimized */}
              {!isMinimized && (
                <div className="mt-6">
                  {/* Beautiful Card-based Layout */}
                  <div className="grid grid-cols-1 gap-4 mb-6">
                    {annData.sections?.map((section, secIndex) =>
                      section.contents?.map((content, index) => {
                        if (!content.content?.trim()) return null;

                        return (
                          <div
                            key={`${secIndex}-${index}`}
                            className="group relative bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:border-secondary/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
                          >
                            {/* Accent Border */}
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-orange-400 rounded-t-xl"></div>

                            {/* Icon */}
                            <div className="flex items-center space-x-3 mb-3">
                              {/*
                              <div className="w-10 h-10 bg-gradient-to-br from-secondary to-orange-400 rounded-lg flex items-center justify-center shadow-lg">
                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                </svg>
                              </div>
                              */}

                              {/* Status Dot
                              <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                <span className="text-xs text-white/60 font-medium uppercase tracking-wide">Active</span>
                              </div>
                             */}
                            </div>

                            {/* Content */}
                            <div className="space-y-2">
                              <h3
                                className="text-white font-semibold text-base leading-snug group-hover:text-secondary transition-colors duration-200"
                                dangerouslySetInnerHTML={{
                                  __html: content.content,
                                }}
                              />
                              <p className="text-white/70 text-sm">
                                - KnowQuest Team
                              </p>
                            </div>

                            {/* Action Button */}
                            <div className="mt-4 flex justify-end">
                              <button className="text-secondary hover:text-orange-400 transition-colors duration-200 text-sm font-medium">
                                {section.title}
                              </button>
                            </div>
                            {/*
                            <div className="mt-4 flex justify-end">
                              <button className="text-secondary hover:text-orange-400 transition-colors duration-200 text-sm font-medium">
                                Learn More →
                              </button>
                            </div>
                            */}

                            {/* Hover Glow Effect */}
                            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-secondary/5 to-orange-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                          </div>
                        );
                      }),
                    )}
                  </div>

                  {/* Enhanced Call to Action */}
                  {/*
                  <div className="text-center bg-white/5 rounded-xl p-6 border border-white/10">
                    <div className="flex items-center justify-center space-x-3 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-secondary to-orange-400 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-lg">Stay in the Loop</h4>
                        <p className="text-white/70 text-sm">Get notified about new features and improvements</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-center space-x-4">
                      <button className="bg-secondary hover:bg-secondary/90 text-primary px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 hover:scale-105 shadow-lg">
                        Subscribe to Updates
                      </button>
                      <button className="border border-white/30 hover:border-white/50 text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 hover:bg-white/10">
                        View Changelog
                      </button>
                    </div>
                  </div>
                  */}
                </div>
              )}
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-secondary/20 to-transparent rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-orange-400/20 to-transparent rounded-full blur-xl"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Announcements;
